import type {
  ApiParlay,
  PaginationInfo,
  ParlayStats,
  TrackedParlay,
  TrackedParlaysFilters,
} from './types';

// =============================================================================
// Module-level defaults
// =============================================================================

export const DEFAULT_STATS: ParlayStats = {
  total: 0,
  won: 0,
  lost: 0,
  pending: 0,
  voided: 0,
  aiCount: 0,
  manualCount: 0,
  betCount: 0,
};

export const DEFAULT_PAGINATION: PaginationInfo = {
  page: 1,
  limit: 100,
  total: 0,
  totalPages: 0,
  hasMore: false,
};

// Default filters: show all results and all origins
export const DEFAULT_FILTERS: TrackedParlaysFilters = {
  results: ['won', 'lost', 'pending', 'void'],
  origins: ['ai', 'manual', 'bet'],
};

/**
 * The query params for the filters the server applies. A results or origins filter that selects
 * every value (or none) is left out, since the server then returns everything.
 */
export function serverFilterParams(filters: TrackedParlaysFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.results && filters.results.length > 0 && filters.results.length < 4) {
    params.results = filters.results.join(',');
  }
  if (filters.origins && filters.origins.length > 0 && filters.origins.length < 3) {
    params.origins = filters.origins.join(',');
  }
  if (filters.sportsbooks && filters.sportsbooks.length > 0) {
    params.sportsbooks = filters.sportsbooks.join(',');
  }
  return params;
}

// =============================================================================
// Data transformation
// =============================================================================

// Convert DB parlay format to frontend format
// DB stores dates as UTC, display in user's local timezone
export function dbToFrontend(dbParlay: ApiParlay): TrackedParlay {
  return {
    id: dbParlay.id,
    // Store raw timestamp for formatting at display time
    createdAt: dbParlay.createdAt || new Date().toISOString(),
    tier: dbParlay.tier,
    name: dbParlay.name,
    totalOdds: dbParlay.totalOdds,
    finalOdds: dbParlay.finalOdds,
    confidence: dbParlay.confidence,
    reasoning: dbParlay.reasoning,
    // Origins array - which sources the parlay's legs came from
    origins: dbParlay.origins,
    isTracked: dbParlay.isTracked ?? true, // If from tracked parlays, default to true
    isAIImproved: dbParlay.isAIImproved ?? false,
    isBetVerified: dbParlay.isBetVerified ?? false,
    stake: dbParlay.stake ? parseFloat(String(dbParlay.stake)) : null,
    result: dbParlay.result || 'pending',
    payout: dbParlay.actualPayout ? parseFloat(String(dbParlay.actualPayout)) : null,
    settledAt: dbParlay.settledAt,
    isSgp: dbParlay.isSgp || false,
    sportsbook: dbParlay.sportsbook,
    // Scores locked at track time
    trackedStrategyScore: dbParlay.trackedStrategyScore
      ? parseFloat(String(dbParlay.trackedStrategyScore))
      : null,
    trackedPredictionScore: dbParlay.trackedPredictionScore
      ? parseFloat(String(dbParlay.trackedPredictionScore))
      : null,
    trackedStrategyRec: dbParlay.trackedStrategyRec ?? null,
    trackedPredictionRec: dbParlay.trackedPredictionRec ?? null,
    trackedLabelTrusted: dbParlay.trackedLabelTrusted ?? null,
    trackedScoreBreakdown: dbParlay.trackedScoreBreakdown ?? null,
    league: dbParlay.league,
    // Legs - now using consistent field names from DB
    legs: (dbParlay.legs || []).map((leg) => ({
      id: leg.id,
      player: leg.player,
      playerId: leg.playerId,
      team: leg.team,
      opponentTeam: leg.opponentTeam,
      prop: leg.prop, // Full display string from DB
      statType: leg.statType, // Normalized stat type from DB
      line: leg.line ? parseFloat(leg.line) : null,
      overUnder: leg.overUnder ?? null,
      odds: leg.odds,
      hit: leg.hit,
      dnp: leg.dnp || false,
      actual: leg.actualValue ? parseFloat(leg.actualValue) : null,
      settled: !!leg.settledAt,
      gameId: leg.gameId,
      gameDate: leg.gameDate, // Game date for duplicate detection
      betContext: leg.betContext, // ML training data for strict scoring
      aiEdge: leg.aiEdge, // AI edge for 4-way combo scoring
      // Phase 4: Fatigue & game context
      isB2b: leg.isB2b ?? null,
      blowoutRisk: leg.blowoutRisk ?? null,
      isHomeGame: leg.isHomeGame ?? null,
      leagueKey: leg.leagueKey,
    })),
  };
}
